import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { withStyles, useTheme } from '@material-ui/core'
import TimeseriesBase, {
	TimeseriesAxis
} from '../../../timeseries'
import {
	getLang,
} from '../../app/selectors/lang'
import useStaticData from '../../../data/useStaticData'
import { getTimeseriesOptions } from '../style'
import HintIconButton from '../../../../shared/components/Buttons/HintIconButton'

const styles = theme => ({
	root: {
		'& .timeseries__tooltip--content': {
			fontFamily: 'inherit'
		},
		'& .timeseries__tooltip--year': {
			fontSize: 14,
			fontWeight: 900,
		},
		'& .timeseries__tooltip--value': {
			display: 'flex',
			justifyContent: 'space-between',
			fontSize: 14,
			'& > div:first-child': {
				fontWeight: 500
			},
			'& > div:last-child': {
				fontWeight: 900
			}
		},
		'& .timeseries__tooltip--unit': {
			fontSize: 12,
			fontWeight: 300
		}
	},
	axis: {
		color: '#757575',
		position: 'absolute',
		fontSize: 8,
		textTransform: 'uppercase'
	},
	yAxis: {
		// left: theme.spacing(-2),
		left: 0,
		top: 0,
		bottom: 0,
		width: 0,
		// reposition y axis for smaller margins on mobile
		[theme.breakpoints.down('sm')]: {
			right: theme.spacing(-3)
		}
	},
})

function SedaTimeseriesBase({
	className,
	classes,
	variant,
	// xAxisLabel,
	yAxisLabel,
	// xAxisChildren,
	// yAxisChildren,
	children,
	onHover,
	onClick,
	onReady,
	allData,
	data,
	trendData,
	vars,
	extents,
	colorExtent,
	// region,
	metric,
	unit,
	...props
}) {
	const theme = useTheme()
	// boolean indicating if data is loading
	const loading = useStaticData(state => state.isLoading)
	const [xVar, yVar] = vars

	/** Add chart variables to hover callback */
	const handleHover = useCallback(
		(e, eChartInstance) => {
			onHover && onHover(e, eChartInstance, vars)
		},
		[onHover, vars]
	)
	// memoize the timeseries options
	const options = useMemo(() => {
		return getTimeseriesOptions({
			allData, // entire region dataset, unfiltered
			data, // filtered data
			trendData,
			xVar,
			yVar,
			extents,
			colorExtent,
			metric,
			unit,
			theme
		})
	}, [
		variant,
		allData,
		data,
		trendData,
		xVar,
		yVar,
		extents,
		colorExtent,
		metric,
		unit,
		theme
	])

	const yAxisTooltip = getLang(`HINT_TIMESERIES_${metric}`)

	return (
		<div
			role='img'
			aria-label={getLang('UI_CHART_TS', {
				yVar: yAxisLabel,
			})}
			className={clsx('timeseries', classes.root, className)}
			{...props}>
			<TimeseriesBase
				theme={theme}
				loading={loading}
				options={options || {}}
				classes={{ error: 'timeseries__error' }}
				onHover={handleHover}
				onClick={onClick}
				onReady={onReady}
			/>
			{children}
			<TimeseriesAxis
				className={clsx(
					'timeseries__axis--y',
					classes.axis,
					classes.yAxis
				)}
				classes={{
					labelContainer: classes.endLabels
				}}
				vertical
				showLabels={false}
				label={yAxisLabel}>
				{yAxisTooltip ?
					<HintIconButton
						title={yAxisTooltip}
					/>
				: null}
			</TimeseriesAxis>
		</div>
	)
}

SedaTimeseriesBase.defaultProps = {
	data: [],
	vars: [],
	extents: [],
	onHover: () => {},
	onClick: () => {},
	onReady: () => {}
}

SedaTimeseriesBase.propTypes = {
	xVar: PropTypes.string,
	yVar: PropTypes.string,
	zVar: PropTypes.string,
	region: PropTypes.string,
	data: PropTypes.array,
	selected: PropTypes.array,
	hovered: PropTypes.object,
	variant: PropTypes.string,
	children: PropTypes.node,
	onHover: PropTypes.func,
	onClick: PropTypes.func,
	onReady: PropTypes.func,
	onError: PropTypes.func,
	error: PropTypes.string
}

export default withStyles(styles)(SedaTimeseriesBase)
